import { ArrowLeft, RotateCw, SaveIcon, TriangleAlert } from 'lucide-react';
import { FC, useCallback } from 'react';
import z from 'zod';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import ErrorComponent from '@/modules/common/components/error-component';
import LoadingContainer from '@/modules/common/components/loading-container';

import { UnsavedChangesAlertDialog } from '../components';
import {
  EVENT_DETAILS_HISTORY_TABS,
  EVENT_DETAILS_TABLE_TABS,
  EventDetailsTab,
} from '../constants';
import { useEventDetailsHandler, useEventDetailsTabs } from '../hooks';
import { EventFormSchema } from '../validations';

export type EventFormSchemaType = z.infer<typeof EventFormSchema>;

export const EventDetailsPage: FC = () => {
  const {
    details,
    error,
    form,

    isLoading,
    unsavedChangesModalProps,
    isUpdateLoading,
    disabledSaveButton,
    refetchEvent,
    onFormSubmit,
    onReset,
  } = useEventDetailsHandler();

  const {
    tabsConfig,
    activeTab,
    visibleTabs,
    isFetchingTypes,
    onChangeTab,
    onBack,
  } = useEventDetailsTabs({
    selectedTypes: details?.types || [],
    eventSystemPrompt: details?.eventSystemPrompt ?? null,
    errorMessages: details?.errorMessages ?? null,
    videoFileObject: details?.videoFileObject,
    idleStateVideoFileObject: details?.idleStateVideoFileObject,
    backgroundImageFileObject: details?.backgroundImageFileObject,
  });

  const isHistoryTab = EVENT_DETAILS_HISTORY_TABS.includes(activeTab);
  const isTableTab = EVENT_DETAILS_TABLE_TABS.includes(activeTab);

  const isTabWarning = useCallback(
    (tab: EventDetailsTab) => {
      const validations = Object.keys(details?.validationErrors || {});
      return validations.includes(tab);
    },
    [details?.validationErrors]
  );

  if (error) {
    return <ErrorComponent message={error.message} onRetry={refetchEvent} />;
  }

  return (
    <LoadingContainer loading={isFetchingTypes || isLoading}>
      <div className="pb-[100px]">
        <header className="sticky top-0 left-0 z-9  h-14 flex w-full items-center border-b bg-background px-2">
          <div className="flex items-center">
            <Button variant="ghost" onClick={onBack}>
              <ArrowLeft />
            </Button>
            <p className="font-bold text-lg text-zinc-50">{details?.name}</p>
          </div>

          <div className="flex flex-1 items-center" />
        </header>

        <div className="p-4">
          <Form {...form}>
            <Tabs
              defaultValue={EventDetailsTab.Info}
              value={activeTab}
              onValueChange={(tab: EventDetailsTab) => onChangeTab(tab)}
            >
              <TabsList className="border-none">
                {tabsConfig.map((tab) => {
                  if (!visibleTabs.includes(tab.value)) {
                    return null;
                  }
                  return (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="min-w-none text-sm gap-1"
                    >
                      {isTabWarning(tab.value) && (
                        <TriangleAlert size={16} className="text-error-700" />
                      )}
                      {tab.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              {tabsConfig.map(({ value, title, Component, noWrapper, componentProps }) => {
                return (
                  <TabsContent key={value} value={value}>
                    <div className="flex flex-col gap-5">
                      {noWrapper ? (
                        <Component {...(componentProps ?? {})} />
                      ) : (
                        <div
                          className={cn(
                            !isTableTab &&
                              'border rounded-lg border-zinc-800 p-6'
                          )}
                        >
                          <h3 className="text-lg font-semibold mb-4">
                            {title}
                          </h3>
                          <Component {...(componentProps ?? {})} />
                        </div>
                      )}
                    </div>

                    {!isHistoryTab && (
                      <div className="w-full flex  gap-4 justify-end fixed bottom-0 left-0 border-t border-zinc-800 bg-zinc-950 p-4 z-10">
                        <Button
                          className="w-fit self-end"
                          variant="outline"
                          disabled={disabledSaveButton}
                          startIcon={<RotateCw />}
                          onClick={onReset}
                        >
                          Reset changes
                        </Button>
                        <Button
                          className="w-fit self-end"
                          onClick={() => {
                            onFormSubmit();
                          }}
                          isLoading={isUpdateLoading}
                          startIcon={<SaveIcon />}
                          disabled={disabledSaveButton}
                        >
                          Save changes
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          </Form>
        </div>

        <UnsavedChangesAlertDialog {...unsavedChangesModalProps} />
      </div>
    </LoadingContainer>
  );
};
